/**
 * Festival retention from people.yearsAttended.
 * Faculty-only records and merged stubs are out. Canceled years should
 * already be absent from the stored list.
 */

function yearsOf (person) {
  const raw = person && person.yearsAttended
  const list = Array.isArray(raw) ? raw : []
  const out = []
  const seen = new Set()
  for (const v of list) {
    const n = Number(v)
    if (!Number.isFinite(n) || n < 2000 || seen.has(n)) {
      continue
    }
    seen.add(n)
    out.push(n)
  }
  out.sort((a, b) => a - b)
  return out
}

function isMergedStub (person) {
  if (!person) {
    return true
  }
  if (person.mergedInto) {
    return true
  }
  const email = String(person.email || '').toLowerCase()
  return email.indexOf('merged+') === 0 || email.indexOf('@iabf.invalid') !== -1
}

export function dancerPeople (people) {
  const out = []
  for (const person of people || []) {
    if (isMergedStub(person) || person.isFaculty) {
      continue
    }
    if (!yearsOf(person).length) {
      continue
    }
    out.push(person)
  }
  return out
}

function hasGap (years) {
  if (years.length < 2) {
    return false
  }
  for (let i = 1; i < years.length; i++) {
    if (years[i] - years[i - 1] > 1) {
      return true
    }
  }
  return false
}

export function computeRetention (people, { focusYear } = {}) {
  const dancers = dancerPeople(people)
  const uniqueCount = dancers.length
  const tenures = {}
  let tenureSum = 0
  const byFirstYear = {}
  const yearSet = new Set()

  for (const person of dancers) {
    const years = yearsOf(person)
    const n = years.length
    tenureSum += n
    tenures[n] = (tenures[n] || 0) + 1
    const first = years[0]
    if (!byFirstYear[first]) {
      byFirstYear[first] = []
    }
    byFirstYear[first].push(years)
    for (const y of years) {
      yearSet.add(y)
    }
  }

  const allYears = Array.from(yearSet).sort((a, b) => a - b)
  const cohortSurvival = Object.keys(byFirstYear)
    .map(Number)
    .sort((a, b) => a - b)
    .map((startYear) => {
      const rows = byFirstYear[startYear]
      const returned = {}
      for (const y of allYears) {
        if (y < startYear) {
          continue
        }
        returned[y] = rows.filter((years) => years.indexOf(y) !== -1).length
      }
      return { startYear, startCount: rows.length, returned }
    })

  const focus = Number(focusYear)
  const prior = Number.isFinite(focus) && focus > 0 ? focus - 1 : null
  const inFocus = Number.isFinite(focus)
    ? dancers.filter((p) => yearsOf(p).indexOf(focus) !== -1)
    : []
  const inPrior = prior
    ? dancers.filter((p) => yearsOf(p).indexOf(prior) !== -1)
    : []
  const yoyRepeatCount = inPrior.filter((p) => yearsOf(p).indexOf(focus) !== -1).length
  const firstTimersPrior = prior
    ? dancers.filter((p) => yearsOf(p)[0] === prior)
    : []
  const secondYearCount = firstTimersPrior.filter((p) => yearsOf(p).indexOf(focus) !== -1).length
  const newCount = inFocus.filter((p) => yearsOf(p)[0] === focus).length
  const stoppedAfterPrior = prior
    ? dancers.filter((p) => {
      const years = yearsOf(p)
      return years[years.length - 1] === prior
    }).length
    : 0
  const reactivatedCount = dancers.filter((p) => hasGap(yearsOf(p))).length

  const loyalty = { first: 0, occasional: 0, regular: 0 }
  for (const person of inFocus) {
    const n = yearsOf(person).length
    if (n <= 1) {
      loyalty.first += 1
    } else if (n <= 3) {
      loyalty.occasional += 1
    } else {
      loyalty.regular += 1
    }
  }

  const tenureRows = Object.keys(tenures)
    .map(Number)
    .sort((a, b) => a - b)
    .map((n) => ({ years: n, count: tenures[n] }))

  return {
    uniqueCount,
    avgTenure: uniqueCount ? tenureSum / uniqueCount : 0,
    tenureRows,
    allYears,
    cohortSurvival,
    yoy: {
      focusYear: focus,
      priorYear: prior,
      priorCount: inPrior.length,
      returnedCount: yoyRepeatCount,
      rate: inPrior.length ? yoyRepeatCount / inPrior.length : null,
      thisYearCount: inFocus.length,
      newCount,
      returningCount: inFocus.length - newCount
    },
    secondYear: {
      firstYear: prior,
      starters: firstTimersPrior.length,
      returned: secondYearCount,
      rate: firstTimersPrior.length ? secondYearCount / firstTimersPrior.length : null
    },
    dropoff: {
      lastYear: prior,
      count: stoppedAfterPrior
    },
    reactivatedCount,
    loyalty
  }
}

export function formatRate (rate) {
  if (rate == null || !Number.isFinite(rate)) {
    return '—'
  }
  return Math.round(rate * 100) + '%'
}
